import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  WINDOW, neighbours, placeFromState, placeState, placeText, windowOffset,
} from "@/lib/contact-pager";
import { useContactWindow } from "@/lib/queries";

/**
 * "3 of 12,142 ‹ ›" over a contact opened from the contacts list, as GoHighLevel's record has: the
 * arrows open the previous and the next contact of that same list (its search, filters and order),
 * on the same tab. A contact opened any other way (a link, the phone) has no list, and no pager.
 */
export function ContactPager({ contactId }: { contactId: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const place = placeFromState(location.state);
  const offset = windowOffset(place?.index ?? 0);
  const rows = useContactWindow(place?.filters ?? {}, offset, WINDOW, place !== null);
  if (!place) return null;
  const at = rows.data ? neighbours(rows.data.items.map((r) => r.id), offset, contactId) : null;
  const total = rows.data?.total ?? null;
  const go = (id: string, index: number) =>
    navigate({ pathname: `/contacts/${id}`, search: location.search }, { state: placeState({ ...place, index }) });
  /** One step through the list. A window older than its fresh time is read again first, so the
   *  contact opened is the one next to this one in the list as it is now. */
  const step = async (by: -1 | 1) => {
    const data = rows.isStale ? (await rows.refetch()).data : rows.data;
    const now = data ? neighbours(data.items.map((r) => r.id), offset, contactId) : null;
    const target = by < 0 ? now?.prev : now?.next;
    if (now && target) go(target, now.index + by);
  };
  return (
    <div className="flex items-center gap-1">
      <span className="min-w-16 text-right text-xs tabular-nums text-text-muted" aria-live="polite">
        {at ? placeText(at.index, total) : ""}
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Previous contact"
        title="Previous contact"
        disabled={!at?.prev || rows.isFetching}
        onClick={() => void step(-1)}
      >
        <ChevronLeft />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Next contact"
        title="Next contact"
        disabled={!at?.next || rows.isFetching}
        onClick={() => void step(1)}
      >
        <ChevronRight />
      </Button>
    </div>
  );
}
